import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { AuthService, type UserProfile } from '@/lib/services/auth';
import type { AuthLoginInput } from '@/types/api';

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (credentials: AuthLoginInput) => Promise<boolean>;
  register: (userData: {
    username: string;
    email: string;
    password: string;
    password_confirm: string;
    first_name?: string;
    last_name?: string;
    phone_number?: string;
  }) => Promise<boolean>;
  logout: () => void;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

const AUTH_STORAGE_KEY = 'cyberrange_auth_user';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Load user from token/profile or localStorage on mount
  useEffect(() => {
    let mounted = true;
    (async () => {
      setIsLoading(true);
      try {
        const token = AuthService.getAccessToken();
        if (token) {
          // If a token exists, try to fetch profile to restore user state
          try {
            const profile = await AuthService.getProfile();
            if (profile && mounted) {
              setUser(profile);
              try {
                localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(profile));
              } catch {}
            }
          } catch (err) {
            // Token invalid or profile fetch failed: do not immediately remove token (avoid auto-logout)
            // Fallback to any stored user so reload doesn't force logout on transient errors.
            console.warn('Failed to fetch profile during init, keeping token and falling back to stored user', err);
            const storedUser = localStorage.getItem(AUTH_STORAGE_KEY);
            if (storedUser) {
              try {
                const parsed = JSON.parse(storedUser) as UserProfile;
                if (mounted) setUser(parsed);
              } catch (parseErr) {
                console.error('Failed to parse stored user during fallback:', parseErr);
                if (mounted) setUser(null);
              }
            } else {
              // leave user null but don't remove token; application can attempt background refresh later
              if (mounted) setUser(null);
            }
          }
        } else {
          // No token: fallback to stored user (if any)
          const storedUser = localStorage.getItem(AUTH_STORAGE_KEY);
          if (storedUser) {
            try {
              const parsed = JSON.parse(storedUser) as UserProfile;
              if (mounted) setUser(parsed);
            } catch (error) {
              console.error('Failed to parse stored user:', error);
              localStorage.removeItem(AUTH_STORAGE_KEY);
            }
          }
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, []);

  const login = async (credentials: AuthLoginInput): Promise<boolean> => {
    try {
      // Support both email and username login
      const loginData = await AuthService.login(
        credentials.email || credentials.username || '',
        credentials.password
      );
      
      if (loginData.user) {
        // Fetch full profile
        try {
          const profile = await AuthService.getProfile();
          setUser(profile);
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(profile));
          return true;
        } catch (err) {
          // If profile fetch fails, use user data from login response
          const userData: UserProfile = {
            id: loginData.user.id,
            username: loginData.user.username,
            email: loginData.user.email,
            first_name: loginData.user.first_name,
            last_name: loginData.user.last_name,
            is_verified: loginData.user.is_verified,
            is_active: true,
            is_staff: false,
            date_joined: new Date().toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          setUser(userData);
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userData));
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Login error:', error);
      return false;
    }
  };

  const register = async (userData: {
    username: string;
    email: string;
    password: string;
    password_confirm: string;
    first_name?: string;
    last_name?: string;
    phone_number?: string;
  }): Promise<boolean> => {
    try {
      const registerData = await AuthService.register(userData);
      
      if (registerData.user) {
        // Fetch full profile
        try {
          const profile = await AuthService.getProfile();
          setUser(profile);
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(profile));
          return true;
        } catch (err) {
          // If profile fetch fails, use user data from register response
          const userProfile: UserProfile = {
            id: registerData.user.id,
            username: registerData.user.username,
            email: registerData.user.email,
            first_name: registerData.user.first_name || '',
            last_name: registerData.user.last_name || '',
            is_verified: false,
            is_active: true,
            is_staff: false,
            date_joined: new Date().toISOString(),
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString(),
          };
          setUser(userProfile);
          localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(userProfile));
          return true;
        }
      }
      return false;
    } catch (error) {
      console.error('Register error:', error);
      return false;
    }
  };

  const refreshUser = async (): Promise<void> => {
    try {
      const profile = await AuthService.getProfile();
      setUser(profile);
      localStorage.setItem(AUTH_STORAGE_KEY, JSON.stringify(profile));
    } catch (error) {
      console.error('Refresh user error:', error);
      // If refresh fails, clear user but don't logout (token might still be valid)
      setUser(null);
    }
  };

  const logout = async () => {
    await AuthService.logout();
    setUser(null);
    localStorage.removeItem(AUTH_STORAGE_KEY);
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isAuthenticated: !!user,
        isLoading,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

